/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { ReactSelectOption } from 'src/types/ReactSelectOption';
import { DataEntryMode } from '../../types/DataEntryMode';
import { ExitAdjustmentFileListItemModel } from '../../types/ExitAdjustmentFileListItemModel';
import { ExitAdjustmentFormViewModel } from '../../types/ExitAdjustmentFormViewModel';
import { ExitAdjustmentListItemModel } from '../../types/ExitAdjustmentListItemModel';
import { ExitAdjustmentModel } from '../../types/ExitAdjustmentModel';
import { StudentModel } from '../../types/StudentModel';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  deleteExitAdjustmentFileThunk,
  fetchAllExitAdjustmentFilesThunk,
  fetchExitAdjustmentById,
  fetchExitAdjustmentFilesByRequestThunk,
  fetchExitAdjustmentFilesByStudentThunk,
  fetchExitAdjustmentFormViewModel,
  fetchExitAdjustmentLeas,
  fetchExitAdjustmentListItems,
  fetchExitAdjustmentSchoolsByLeaId,
  fetchExitAdjustmentSchoolYears,
  fetchExitAdjustmentStudentUpdate,
  fetchStudentByMSISId,
  uploadExitAdjustmentFileThunk,
} from '../thunks/exitAdjustmentThunks';

interface ExitAdjustmentState {
  schoolYear: string;
  school: string;
  loadingListItems: boolean;
  listItems: ExitAdjustmentListItemModel[];

  loadingExitAdjustment: boolean;
  exitAdjustment: ExitAdjustmentModel;

  loadingFormViewModel: boolean;
  formViewModel: ExitAdjustmentFormViewModel;

  dataEntryMode: DataEntryMode;

  // Search-related state
  searchError: string;
  hasSearchMatch: boolean;
  searchResults: ExitAdjustmentModel | StudentModel;

  uploadingFile: boolean;
  downloadingFile: boolean;
  deletingFile: boolean;
  files: ExitAdjustmentFileListItemModel[];
  studentFiles: ExitAdjustmentFileListItemModel[];
  loadingFiles: boolean;
  loadingStudentFiles: boolean;
  error: string | null;
}

export const defaultListState: ExitAdjustmentListItemModel[] = [];

export const defaultExitAdjustmentState: ExitAdjustmentModel = {
  id: '',
  studentIdentifierState: '',
  studentLastOrSurname: '',
  studentFirstName: '',
  studentMiddleName: '',
  birthDate: '',
  districtName: '',
  leaIdentifier: '',
  schoolName: '',
  schoolId: '',
  schoolYear: '',
  cohortEntryDate: '',
  lastEnrolledDate: '',
  entryType: '',
  enrollmentExitType: '',
  enrollmentExitDate: '',
  cohortStatus: '',
  updatedExitType: '',
  updatedExitDate: '',
  removalReason: '',
  schoolStudentTransferredTo: '',
  documentsUploadedToSharePoint: false,
  reviewStatus: '',
  denialReason: '',
  studentUpdate: false,
};

export const defaultFormState: ExitAdjustmentFormViewModel = {
  newExitAdjustmentId: '',
  defaultSchoolYear: { value: '', label: '' },
  selectedSchoolYear: { value: '', label: '' },
  schoolYearOptions: [],
  selectedLea: { value: '', label: '' },
  leaOptions: [],
  selectedSchool: { value: '', label: '' },
  schoolOptions: [],
  selectedStatus: { value: '', label: '' },
  cohortStatusOptions: [
    { value: 'Graduate', text: 'Graduate' },
    { value: 'Dropout', text: 'Dropout' },
    { value: 'Still Enrolled', text: 'Still Enrolled' },
    { value: 'Completer', text: 'Completer' },
    {
      value: 'Removed from ExitAdjustment',
      text: 'Removed from ExitAdjustment',
    },
  ],
  statusOptions: [
    { text: '\xa0', value: '', isDisabled: true },
    {
      text: 'Pending Approval',
      value: 'Pending Approval',
      isDisabled: true,
    },
    {
      text: 'Approved',
      value: 'Approved',
      isDisabled: false,
    },
    {
      text: 'Denied',
      value: 'Denied',
      isDisabled: false,
    },
  ],
  removalReasonOptions: [
    { text: 'Transferred out of State', value: 'Transferred out of State' },
    { text: 'Transferred out of Country', value: 'Transferred out of Country' },
    { text: 'Transferred to Home School', value: 'Transferred to Home School' },
    {
      text: 'Transferred to Private School',
      value: 'Transferred to Private School',
    },
  ],
  studentUpdate: false,
};

export const dataEntryMode = DataEntryMode.NEW;

export const initialState: ExitAdjustmentState = {
  schoolYear: '',
  school: '',
  loadingListItems: false,
  listItems: [],
  loadingExitAdjustment: false,
  exitAdjustment: defaultExitAdjustmentState,
  loadingFormViewModel: false,
  formViewModel: defaultFormState,
  dataEntryMode: dataEntryMode,
  searchError: '',
  hasSearchMatch: false,
  searchResults: defaultExitAdjustmentState,
  uploadingFile: false,
  downloadingFile: false,
  deletingFile: false,
  files: [],
  studentFiles: [],
  loadingFiles: false,
  loadingStudentFiles: false,
  error: null,
};

export const exitAdjustmentSlice = createSlice({
  name: 'exitAdjustment',
  initialState,
  reducers: {
    clearExitAdjustment: (state) => {
      state.exitAdjustment = defaultExitAdjustmentState;
    },
    saveExitAdjustment: (state, action: PayloadAction<ExitAdjustmentModel>) => {
      state.exitAdjustment = action.payload;
    },
    setExitAdjustmentDataEntryMode: (
      state,
      action: PayloadAction<DataEntryMode>
    ) => {
      state.dataEntryMode = action.payload;
    },
    setExitAdjustmentSchoolYear: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedSchoolYear = action.payload;
    },
    setExitAdjustmentLea: (state, action: PayloadAction<ReactSelectOption>) => {
      state.formViewModel.selectedLea = action.payload;
    },
    setExitAdjustmentSchool: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedSchool = action.payload;
    },
    setExitAdjustmentStatus: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedStatus = action.payload;
    },
    clearSearchError: (state) => {
      state.searchError = '';
    },
    setSearchError: (state, action: PayloadAction<string>) => {
      state.searchError = action.payload;
    },
    setHasSearchMatch: (state, action: PayloadAction<boolean>) => {
      state.hasSearchMatch = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = defaultExitAdjustmentState;
    },
    setStudentFiles: (
      state,
      action: PayloadAction<ExitAdjustmentFileListItemModel[]>
    ) => {
      state.studentFiles = action.payload;
    },
    clearExitAdjustmentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchLeaManagementList
    builder.addCase(fetchExitAdjustmentListItems.pending, (state) => {
      state.listItems = defaultListState;
      state.loadingListItems = true;
    });
    builder.addCase(fetchExitAdjustmentListItems.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload) && Array.isArray(action.payload)) {
        state.listItems = action.payload;
      }
      state.loadingListItems = false;
    });
    builder.addCase(fetchExitAdjustmentListItems.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingListItems = false;
      }
    });

    // fetchExitAdjustmentById
    builder.addCase(fetchExitAdjustmentById.pending, (state) => {
      state.exitAdjustment = defaultExitAdjustmentState;
      state.loadingExitAdjustment = true;
    });
    builder.addCase(fetchExitAdjustmentById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.exitAdjustment = action.payload;
      }
      state.loadingExitAdjustment = false;
    });
    builder.addCase(fetchExitAdjustmentById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingExitAdjustment = false;
      }
    });

    // fetchExitAdjustmentFormViewModel
    builder.addCase(fetchExitAdjustmentFormViewModel.pending, (state) => {
      state.formViewModel = defaultFormState;
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchExitAdjustmentFormViewModel.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel = {
            ...state.formViewModel,
            ...action.payload,
          };
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchExitAdjustmentFormViewModel.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetch School Year options
    builder.addCase(fetchExitAdjustmentSchoolYears.pending, (state) => {
      state.formViewModel.schoolYearOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchExitAdjustmentSchoolYears.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload) && Array.isArray(action.payload)) {
          state.formViewModel.schoolYearOptions = action.payload;
          const defaultValue = action.payload[0] || {
            value: '',
            label: '',
            text: '',
          };

          state.formViewModel.defaultSchoolYear = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
          state.formViewModel.selectedSchoolYear = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchExitAdjustmentSchoolYears.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetch LEA options
    builder.addCase(fetchExitAdjustmentLeas.pending, (state) => {
      state.formViewModel.schoolYearOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(fetchExitAdjustmentLeas.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload) && Array.isArray(action.payload)) {
        state.formViewModel.leaOptions = action.payload;
      }
      state.loadingFormViewModel = false;
    });
    builder.addCase(fetchExitAdjustmentLeas.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingFormViewModel = false;
      }
    });

    //fetch Student Update
    builder.addCase(fetchExitAdjustmentStudentUpdate.pending, (state) => {
      state.formViewModel.studentUpdate = false;
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchExitAdjustmentStudentUpdate.fulfilled,
      (state, action) => {
        if (isBoolean(action.payload)) {
          state.formViewModel.studentUpdate = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchExitAdjustmentStudentUpdate.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetch School options
    builder.addCase(fetchExitAdjustmentSchoolsByLeaId.pending, (state) => {
      state.formViewModel.schoolOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchExitAdjustmentSchoolsByLeaId.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload) && Array.isArray(action.payload)) {
          state.formViewModel.schoolOptions = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchExitAdjustmentSchoolsByLeaId.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    // fetchStudentByMSISId
    builder.addCase(fetchStudentByMSISId.pending, (state) => {
      state.searchResults = defaultExitAdjustmentState;
      state.loadingExitAdjustment = true;
      state.searchError = '';
      state.hasSearchMatch = false;
    });
    builder.addCase(fetchStudentByMSISId.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.searchResults = action.payload;
        state.hasSearchMatch = true;
        state.searchError = '';
      }
      state.loadingExitAdjustment = false;
    });
    builder.addCase(fetchStudentByMSISId.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingExitAdjustment = false;
        state.hasSearchMatch = false;

        // Handle specific error cases
        if (action.error.message?.includes('409')) {
          state.searchError =
            'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again.';
        } else if (action.error.message?.includes('422')) {
          state.searchError =
            'This student is not in an active exitAdjustment.  Please check the MSIS ID and try again.';
        } else {
          state.searchError =
            'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.';
        }
      }
    });

    builder.addCase(uploadExitAdjustmentFileThunk.pending, (state) => {
      state.uploadingFile = true;
      state.error = null;
    });
    builder.addCase(uploadExitAdjustmentFileThunk.fulfilled, (state) => {
      state.uploadingFile = false;
    });
    builder.addCase(uploadExitAdjustmentFileThunk.rejected, (state, action) => {
      state.uploadingFile = false;
      state.error = action.error.message || 'Failed to upload file';
    });

    builder.addCase(deleteExitAdjustmentFileThunk.pending, (state) => {
      state.deletingFile = true;
      state.error = null;
    });
    builder.addCase(deleteExitAdjustmentFileThunk.fulfilled, (state) => {
      state.deletingFile = false;
    });
    builder.addCase(deleteExitAdjustmentFileThunk.rejected, (state, action) => {
      state.deletingFile = false;
      state.error = action.error.message || 'Failed to delete file';
    });

    builder.addCase(fetchAllExitAdjustmentFilesThunk.pending, (state) => {
      state.loadingFiles = true;
      state.error = null;
    });
    builder.addCase(
      fetchAllExitAdjustmentFilesThunk.fulfilled,
      (state, action) => {
        state.files = action.payload || [];
        state.loadingFiles = false;
      }
    );
    builder.addCase(
      fetchAllExitAdjustmentFilesThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFiles = false;
          state.error = action.error.message || 'Failed to fetch files';
        }
      }
    );

    builder.addCase(fetchExitAdjustmentFilesByStudentThunk.pending, (state) => {
      state.loadingStudentFiles = true;
      state.error = null;
    });
    builder.addCase(
      fetchExitAdjustmentFilesByStudentThunk.fulfilled,
      (state, action) => {
        state.studentFiles = action.payload || [];
        state.loadingStudentFiles = false;
      }
    );
    builder.addCase(
      fetchExitAdjustmentFilesByStudentThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingStudentFiles = false;
          state.error = action.error.message || 'Failed to fetch student files';
        }
      }
    );

    builder.addCase(fetchExitAdjustmentFilesByRequestThunk.pending, (state) => {
      state.loadingStudentFiles = true;
      state.error = null;
    });
    builder.addCase(
      fetchExitAdjustmentFilesByRequestThunk.fulfilled,
      (state, action) => {
        state.studentFiles = action.payload || [];
        state.loadingStudentFiles = false;
      }
    );
    builder.addCase(
      fetchExitAdjustmentFilesByRequestThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingStudentFiles = false;
          state.error = action.error.message || 'Failed to fetch request files';
        }
      }
    );
  },
});

//export reducers
export const {
  clearExitAdjustment,
  saveExitAdjustment,
  setExitAdjustmentDataEntryMode,
  setExitAdjustmentSchoolYear,
  setExitAdjustmentLea,
  setExitAdjustmentSchool,
  setExitAdjustmentStatus,
  clearSearchError,
  setSearchError,
  setHasSearchMatch,
  clearSearchResults,
  setStudentFiles,
  clearExitAdjustmentError,
} = exitAdjustmentSlice.actions;

// listItems
export const selectExitAdjustmentLoadingListItems = (
  state: RootState
): boolean => state.exitAdjustment.loadingListItems;
export const selectExitAdjustmentListItems = (
  state: RootState
): ExitAdjustmentListItemModel[] => state.exitAdjustment.listItems;

// exitAdjustment
export const selectExitAdjustmentLoadingExitAdjustment = (
  state: RootState
): boolean => state.exitAdjustment.loadingExitAdjustment;
export const selectExitAdjustmentExitAdjustment = (
  state: RootState
): ExitAdjustmentModel => state.exitAdjustment.exitAdjustment;

// formViewModel
export const selectExitAdjustmentLoadingFormViewModel = (
  state: RootState
): boolean => state.exitAdjustment.loadingFormViewModel;

export const selectExitAdjustmentFormViewModel = (
  state: RootState
): ExitAdjustmentFormViewModel => state.exitAdjustment.formViewModel;

// dataEntryMode
export const selectExitAdjustmentDataEntryMode = (
  state: RootState
): DataEntryMode => state.exitAdjustment.dataEntryMode;

export const selectExitAdjustmentSchoolYear = (state: RootState): string =>
  state.exitAdjustment.schoolYear;

// Search-related selectors
export const selectExitAdjustmentSearchError = (state: RootState): string =>
  state.exitAdjustment.searchError;

export const selectExitAdjustmentHasSearchMatch = (state: RootState): boolean =>
  state.exitAdjustment.hasSearchMatch;

export const selectExitAdjustmentSearchResults = (
  state: RootState
): ExitAdjustmentModel | StudentModel => state.exitAdjustment.searchResults;

export const selectExitAdjustmentStudentFiles = (
  state: RootState
): ExitAdjustmentFileListItemModel[] => state.exitAdjustment.studentFiles;

export const selectExitAdjustmentError = (state: RootState): string | null =>
  state.exitAdjustment.error;

export default exitAdjustmentSlice.reducer;
